import React, {
  DetailedHTMLProps,
  ButtonHTMLAttributes,
  forwardRef,
  useMemo,
} from 'react';
import classnames from 'classnames';

export type RSDialogButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  label: string;
  variant?: 'action' | 'cancel' | 'destructive';
  isRoundedBR?: boolean;
  isRoundedBL?: boolean;
};

export const RSDialogButton = forwardRef<
  HTMLButtonElement,
  RSDialogButtonProps
>(
  (
    {
      label,
      variant = 'action',
      isRoundedBR = false,
      isRoundedBL = false,
      ...props
    },
    ref,
  ) => {
    const color = '#0066FF';

    const textColorClasses = useMemo(() => {
      switch (variant) {
        case 'action':
          return [];
        case 'cancel':
          return ['text-gray-1000'];
        case 'destructive':
          return ['text-red-700'];
      }
    }, [variant]);

    const fontWeightClasses = useMemo(() => {
      switch (variant) {
        case 'action':
        case 'destructive':
          return ['font-bold'];
        case 'cancel':
          return ['font-normal'];
      }
    }, [variant]);

    return (
      <>
        <button
          {...props}
          ref={ref}
          className={classnames(
            'button',
            'appearance-none',
            'box-border',
            'relative',
            'block',
            'w-full',
            'h-56',
            'p-0',
            'bg-white',
            'hover:bg-[#0000000a]',
            variant === 'action' && 'text-primary',
            textColorClasses,
            'border-solid',
            'border-2',
            'border-transparent',
            'cursor-pointer',
            'outline-none',
            'focus:outline-none',
            'transition',
            isRoundedBR && 'rounded-br-15',
            isRoundedBL && 'rounded-bl-15',
          )}
        >
          <div className="flex h-full w-full items-center justify-center">
            <div
              className={classnames(
                'rs-font',
                'text-16',
                'pc:text-15',
                fontWeightClasses,
                'whitespace-nowrap',
              )}
            >
              {label}
            </div>
          </div>
        </button>
        <style jsx>{`
          .button:focus-visible {
            border-color: ${color};
          }

          .text-primary {
            color: ${color};
          }
        `}</style>
      </>
    );
  },
);
